import type { OrganizationBillingOverviewDto } from '../api/billingPayments'
import type { CategoryBandDto, PriceMoveDto } from '../api/marketAnalytics'
import type { MatchCandidateDto } from '../api/catalogImportTypes'
import type { NameSuggestionDto, RestaurantPrivateSupplierDto } from '../api/restaurantMyPrices'
import type { ProductTypeDto } from '../api/productTypes'
import type {
  CatalogItemSortDirection,
  CatalogItemSortKey,
  CatalogItemsPageDto,
  CatalogSummaryDto,
  SupplierCatalogFilterOptionsDto,
  SupplierCatalogItemDto,
} from '../api/supplierCatalog'
import type { AuthSession, MeResponse } from '../api/types'
import type { CartLine } from '../cart/types'
import { demoCatalogListIdsForItem } from '../api/catalogLists'

const iso = (daysAgo: number) => {
  const date = new Date()
  date.setDate(date.getDate() - daysAgo)
  return date.toISOString()
}

export const demoProductTypes: ProductTypeDto[] = [
  { id: 1, parentId: null, code: 'meat', name: 'Мясо', active: true },
  { id: 15, parentId: null, code: 'poultry', name: 'Птица', active: true },
  { id: 29, parentId: null, code: 'eggs', name: 'Яйцо', active: true },
  { id: 32, parentId: null, code: 'vegetables', name: 'Овощи', active: true },
  { id: 60, parentId: null, code: 'fruits', name: 'Фрукты', active: true },
  { id: 72, parentId: null, code: 'dairy', name: 'Молочные продукты', active: true },
  { id: 85, parentId: null, code: 'fish', name: 'Рыба', active: true },
  { id: 95, parentId: null, code: 'seafood', name: 'Морепродукты', active: true },
  { id: 105, parentId: null, code: 'groceries', name: 'Бакалея', active: true },
  { id: 2, parentId: 1, code: 'meat.beef', name: 'Говядина', active: true },
  { id: 17, parentId: 15, code: 'poultry.chicken.fillet', name: 'Филе куриное', active: true },
  { id: 34, parentId: 32, code: 'vegetables.root.potato', name: 'Картофель', active: true },
  { id: 40, parentId: 32, code: 'vegetables.leafy.herbs_bunch', name: 'Зелень', active: true },
]

export const demoProductTypesL0Options = (): Array<{ id: number; name: string }> =>
  demoProductTypes
    .filter((type) => type.parentId == null && type.active)
    .map(({ id, name }) => ({ id, name }))

const demoMyPriceItem = (overrides: Partial<SupplierCatalogItemDto> = {}): SupplierCatalogItemDto => ({
  id: 1001,
  organizationId: 10,
  supplierName: 'Филе куриное охлаждённое',
  matchStatus: 'manual',
  externalSku: 'CHK-01',
  brand: 'Петелинка',
  country: 'Россия',
  unit: 'kg',
  price: 279,
  minShipmentQty: null,
  packageSize: null,
  productTypeId: 17,
  productTypeCode: 'poultry',
  productTypeName: 'Птица',
  productTypeActive: true,
  matchConfidence: null,
  operatorComment: null,
  updatedAt: iso(3),
  freshnessStatus: 'fresh',
  daysSinceUpdate: 3,
  buyerSearchVisible: true,
  privateSupplierId: 501,
  privateSupplierName: 'ИП Демо Поставщик',
  // Поля аналитики цен добавили в DTO вместе с my-prices UX, а в фабрику демо-данных
  // не занесли — из-за этого tsc падал, а значит и сборка market целиком.
  previousPrice: null,
  priceDeltaPct: null,
  previousObservedAt: null,
  ...overrides,
})

export const demoMyPricesSummary = (): CatalogSummaryDto => {
  const items = demoMyPricesStore()
  const count = (status: string) => items.filter((item) => item.matchStatus === status).length
  return {
    totalItems: items.length,
    matchAuto: count('auto'),
    matchSuggested: count('suggested'),
    matchManual: count('manual'),
    matchUnmatched: count('unmatched'),
    needsAttention: count('suggested') + count('unmatched'),
    staleItems: items.filter((item) => item.freshnessStatus && item.freshnessStatus !== 'fresh').length,
    hiddenFromSearch: 0,
  }
}

/**
 * Демо «Моих прайсов» изменяемое: быстрые правки в таблице, подтверждение категории и смена
 * поставщика меняют эти же строки, иначе в демо-режиме их не проверить.
 */
const demoMyPricesSeed = (): SupplierCatalogItemDto[] => [
  demoMyPriceItem({ id: 1001 }),
  demoMyPriceItem({
    id: 1002,
    supplierName: 'Говядина вырезка премиум',
    brand: 'Miratorg',
    price: 920,
    productTypeId: 2,
    productTypeName: 'Говядина',
    matchStatus: 'auto',
    freshnessStatus: 'stale_7',
    daysSinceUpdate: 9,
  }),
  demoMyPriceItem({
    id: 1003,
    supplierName: 'Картофель мытый',
    brand: null,
    price: 39,
    productTypeId: 34,
    productTypeName: 'Картофель',
    matchStatus: 'suggested',
    matchConfidence: 0.86,
  }),
  demoMyPriceItem({
    id: 1004,
    supplierName: 'Масло сливочное 82,5%',
    brand: 'Вологодское',
    price: 615,
    productTypeId: 105,
    productTypeName: 'Бакалея',
    matchStatus: 'manual',
  }),
  demoMyPriceItem({
    id: 1005,
    supplierName: 'Руккола мытая',
    brand: null,
    price: 360,
    productTypeId: 40,
    productTypeName: 'Зелень',
    matchStatus: 'auto',
  }),
  demoMyPriceItem({
    id: 1006,
    supplierName: 'Лосось филе',
    brand: 'Nordic',
    price: 1420,
    country: 'Норвегия',
    productTypeId: 1,
    productTypeName: 'Мясо',
    matchStatus: 'suggested',
    freshnessStatus: 'stale_14',
    daysSinceUpdate: 16,
  }),
  demoMyPriceItem({
    id: 1007,
    supplierName: 'Сметана 20%',
    brand: 'Простоквашино',
    price: 118,
    productTypeId: null,
    productTypeName: null,
    matchStatus: 'unmatched',
  }),
  demoMyPriceItem({
    id: 1008,
    supplierName: 'Булочка для бургера',
    brand: 'BakeHouse',
    price: 22,
    unit: 'шт',
    productTypeId: 105,
    productTypeName: 'Бакалея',
    matchStatus: 'manual',
    updatedAt: iso(1),
  }),
]

let demoMyPricesItems: SupplierCatalogItemDto[] | null = null

const demoMyPricesStore = (): SupplierCatalogItemDto[] => {
  demoMyPricesItems ??= demoMyPricesSeed()
  return demoMyPricesItems
}

const demoMyPricesFind = (itemId: number): SupplierCatalogItemDto => {
  const item = demoMyPricesStore().find((candidate) => candidate.id === itemId)
  if (!item) {
    throw new Error('Позиция не найдена')
  }
  return item
}

export const demoMyPricesUpdateFields = (
  itemId: number,
  update: Partial<Pick<SupplierCatalogItemDto, 'supplierName' | 'price' | 'unit' | 'brand' | 'country' | 'packageSize' | 'minShipmentQty' | 'privateSupplierId'>>,
): SupplierCatalogItemDto => {
  const item = demoMyPricesFind(itemId)
  Object.assign(item, Object.fromEntries(Object.entries(update).filter(([, value]) => value !== undefined)))
  if (update.privateSupplierId !== undefined) {
    item.privateSupplierName =
      demoPrivateSuppliers().find((supplier) => supplier.id === update.privateSupplierId)?.legalName ?? null
  }
  item.updatedAt = iso(0)
  return { ...item }
}

export const demoMyPricesAssignProductType = (itemId: number, productTypeId: number): SupplierCatalogItemDto => {
  const item = demoMyPricesFind(itemId)
  const type = demoProductTypes.find((candidate) => candidate.id === productTypeId)
  item.productTypeId = productTypeId
  item.productTypeName = type?.name ?? null
  item.productTypeCode = type?.code ?? null
  item.matchStatus = 'manual'
  return { ...item }
}

export const demoMyPricesConfirmSuggestion = (itemId: number): SupplierCatalogItemDto => {
  const item = demoMyPricesFind(itemId)
  item.matchStatus = 'manual'
  return { ...item }
}

/** «Возможно» в демо: первая другая категория того же отдела — для проверки UI, не матчер. */
export const demoMyPricesCategorySuggestions = (itemId: number): MatchCandidateDto[] => {
  const item = demoMyPricesFind(itemId)
  return demoProductTypes
    .filter((type) => type.id !== item.productTypeId)
    .slice(0, 3)
    .map((type, index) => ({
      productTypeId: type.id,
      code: type.code,
      name: type.name,
      score: 0.8 - index * 0.1,
    }))
}

const demoSharedNames: Array<{ name: string; productTypeName: string | null }> = [
  { name: 'Сыр Российский 50% Пестравка, брус', productTypeName: 'Сыр полутвердый' },
  { name: 'Филе куриное охлаждённое Приосколье', productTypeName: 'Филе куриное' },
  { name: 'Говядина вырезка б/к охл. Мираторг', productTypeName: 'Вырезка говяжья' },
  { name: 'Картофель мытый, сетка 25 кг', productTypeName: 'Картофель' },
  { name: 'Масло сливочное 82,5% Простоквашино', productTypeName: 'Масло сливочное' },
  { name: 'Сметана 20% Простоквашино 540 г', productTypeName: 'Сметана' },
  { name: 'Булочка для бургера с кунжутом 75 г', productTypeName: 'Хлебобулочные изделия' },
]

export const demoMyPricesNameSuggestions = (query: string): NameSuggestionDto[] => {
  const tokens = query.toLowerCase().replace(/ё/g, 'е').split(/\s+/).filter((token) => token.length >= 2)
  if (tokens.length === 0) {
    return []
  }
  const matches = (name: string) => {
    const words = name.toLowerCase().replace(/ё/g, 'е').split(/[^\p{L}\p{N}%,.]+/u)
    return tokens.every((token) => words.some((word) => word.startsWith(token)))
  }
  const own = demoMyPricesStore()
    .filter((item) => matches(item.supplierName))
    .map((item) => ({ name: item.supplierName, productTypeName: item.productTypeName, own: true }))
  const shared = demoSharedNames
    .filter((entry) => matches(entry.name))
    .map((entry) => ({ ...entry, own: false }))
  return [...own, ...shared].slice(0, 6)
}

export const demoMyPricesItemsPage = (options?: {
  page?: number
  size?: number
  matchStatus?: string
  productTypeId?: number | null
  productTypeIds?: number[]
  unassignedProductType?: boolean
  needsAttention?: boolean
  search?: string
  privateSupplierId?: number | null
  catalogListId?: number | null
  sortBy?: CatalogItemSortKey
  sortDirection?: CatalogItemSortDirection
}): CatalogItemsPageDto => {
  // Копии: страница держит строки в состоянии, а правка меняет хранилище на месте.
  const allItems = demoMyPricesStore().map((item) => ({
    ...item,
    catalogListIds: demoCatalogListIdsForItem(item.id),
  }))

  const matchStatus = options?.matchStatus?.trim().toLowerCase() || ''
  const needsAttention = Boolean(options?.needsAttention)
  const search = options?.search?.trim().toLowerCase() || ''
  const typeIds = new Set(
    (options?.productTypeIds?.length
      ? options.productTypeIds
      : options?.productTypeId != null
        ? [options.productTypeId]
        : []
    ).filter((id) => Number.isFinite(id)),
  )
  const unassignedOnly = Boolean(options?.unassignedProductType)
  const privateSupplierId = options?.privateSupplierId ?? null
  const catalogListId = options?.catalogListId ?? null
  const sortBy = options?.sortBy
  const sortDirection = options?.sortDirection ?? 'asc'

  const filtered = allItems.filter((item) => {
    if (catalogListId != null && !(item.catalogListIds ?? []).includes(catalogListId)) {
      return false
    }
    if (privateSupplierId != null && item.privateSupplierId !== privateSupplierId) {
      return false
    }
    if (matchStatus && item.matchStatus.toLowerCase() !== matchStatus) {
      return false
    }
    if (needsAttention && item.matchStatus !== 'suggested' && item.matchStatus !== 'unmatched') {
      return false
    }
    if (unassignedOnly) {
      if (item.productTypeId != null) {
        return false
      }
    } else if (typeIds.size > 0) {
      if (item.productTypeId == null || !typeIds.has(item.productTypeId)) {
        return false
      }
    }
    if (search) {
      const haystack = [item.supplierName, item.brand, item.externalSku]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      if (!haystack.includes(search)) {
        return false
      }
    }
    return true
  })

  if (sortBy) {
    const collator = new Intl.Collator('ru', { numeric: true, sensitivity: 'base' })
    const valueFor = (item: SupplierCatalogItemDto): string | number | null => {
      switch (sortBy) {
        case 'name':
          return item.supplierName
        case 'sku':
          return item.externalSku
        case 'price':
          return item.price
        case 'unit':
          return item.unit
        case 'package':
          return item.packageSize
        case 'minShipment':
          return item.minShipmentQty
        case 'brand':
          return item.brand
        case 'country':
          return item.country
        case 'category':
          return item.productTypeName
        case 'updated':
          return item.updatedAt
        case 'match':
          return item.matchStatus
        case 'supplier':
          return item.privateSupplierName
      }
    }
    filtered.sort((left, right) => {
      const leftValue = valueFor(left)
      const rightValue = valueFor(right)
      const leftEmpty = leftValue == null || leftValue === ''
      const rightEmpty = rightValue == null || rightValue === ''
      if (leftEmpty || rightEmpty) return Number(leftEmpty) - Number(rightEmpty)
      const comparison =
        typeof leftValue === 'number' && typeof rightValue === 'number'
          ? leftValue - rightValue
          : collator.compare(String(leftValue), String(rightValue))
      return sortDirection === 'asc' ? comparison : -comparison
    })
  }

  const size = options?.size ?? 50
  const page = options?.page ?? 0
  const start = page * size
  const items = filtered.slice(start, start + size)
  const totalElements = filtered.length
  const totalPages = Math.max(1, Math.ceil(totalElements / size))

  return {
    items,
    page,
    size,
    totalElements,
    totalPages,
  }
}

export const demoMyPricesFilterOptions = (): SupplierCatalogFilterOptionsDto => ({
  brands: ['Петелинка', 'Miratorg', 'Вологодское', 'Nordic', 'BakeHouse'],
  countries: ['Россия', 'Норвегия'],
})

export const demoBillingOverview = (): OrganizationBillingOverviewDto => ({
  catalog: [
    {
      plan: 'start',
      display_name: 'Старт',
      monthly_rub: 0,
      yearly_rub: null,
      description: 'Мои прайсы и бланк заказа',
      seat_limit: 1,
      base_venue_limit: 1,
      devices_per_user_limit: 1,
      max_private_suppliers: 5,
      private_supplier_refresh_days: 7,
      market_catalog_enabled: false,
      platform_rfq_enabled: false,
      platform_chat_enabled: false,
      buyer_suppliers_enabled: false,
      market_price_intelligence_enabled: false,
    },
    {
      plan: 'month',
      display_name: 'Месяц',
      monthly_rub: 2_990,
      yearly_rub: null,
      description: 'Всё из «Старта» без лимитов на поставщиков и обновление прайсов',
      seat_limit: 3,
      base_venue_limit: 1,
      devices_per_user_limit: 1,
      max_private_suppliers: null,
      private_supplier_refresh_days: null,
      market_catalog_enabled: true,
      platform_rfq_enabled: true,
      platform_chat_enabled: true,
      buyer_suppliers_enabled: true,
      market_price_intelligence_enabled: false,
    },
    {
      plan: 'year',
      display_name: 'Год',
      monthly_rub: 2_908,
      yearly_rub: 34_900,
      description: 'Полный доступ на 12 месяцев, сразу 2 заведения',
      seat_limit: 3,
      base_venue_limit: 2,
      max_employees_per_venue: 2,
      devices_per_user_limit: 1,
      max_private_suppliers: null,
      private_supplier_refresh_days: null,
      market_catalog_enabled: true,
      platform_rfq_enabled: true,
      platform_chat_enabled: true,
      buyer_suppliers_enabled: true,
      market_price_intelligence_enabled: true,
    },
  ],
  addon_venue_monthly_rub: 1_990,
  addon_venue_yearly_rub: 23_880,
  premium_base_venues: 2,
  current: {
    plan: 'year',
    plan_display_name: 'Год',
    subscription_status: 'active',
    subscription_status_display_name: 'Активна',
    billing_interval: 'yearly',
    period_end: '2026-08-13',
    paid_venue_slots: 0,
    seats_used: 2,
    seats_limit: 3,
    venues_used: 1,
    venues_limit: 2,
  },
  premium_quote: {
    venue_count: 3,
    monthly_rub: 4_980,
    yearly_rub: 58_780,
    addon_venue_slots: 1,
  },
})

export const demoCartLines: CartLine[] = [
  {
    catalogItemId: 101,
    supplierKind: 'registered',
    supplierOrganizationId: 20,
    privateSupplierId: null,
    supplierOrganizationName: 'ООО Свежий Склад',
    productTypeId: 17,
    productTypeName: 'Птица',
    itemName: 'Филе куриное',
    brand: 'Петелинка',
    unit: 'kg',
    price: '289.00',
    quantity: 12,
    note: '',
  },
  {
    catalogItemId: 103,
    supplierKind: 'registered',
    supplierOrganizationId: 21,
    privateSupplierId: null,
    supplierOrganizationName: 'ИП Фермеров',
        productTypeId: 34,
    productTypeName: 'Картофель',
    itemName: 'Картофель мытый',
    brand: null,
    unit: 'kg',
    price: '42.00',
    quantity: 30,
    note: 'Средний калибр',
  },
  {
    catalogItemId: 104,
    supplierKind: 'registered',
    supplierOrganizationId: 21,
    privateSupplierId: null,
    supplierOrganizationName: 'ИП Фермеров',
    productTypeId: 105,
    productTypeName: 'Бакалея',
    itemName: 'Масло сливочное 82,5%',
    brand: 'Вологодское',
    unit: 'kg',
    price: '620.00',
    quantity: 5,
    note: '',
  },
  {
    catalogItemId: 999,
    supplierKind: 'external',
    supplierOrganizationId: null,
    privateSupplierId: 501,
    supplierOrganizationName: 'Прайс «Утренний рынок»',
    productTypeId: 40,
    productTypeName: 'Зелень',
    itemName: 'Базилик зелёный',
    brand: null,
    unit: 'kg',
    price: '450.00',
    quantity: 2,
    note: 'Из внешнего прайса',
  },
]

/**
 * Карточки внешних поставщиков для демо-режима.
 *
 * Три записи намеренно покрывают три состояния минимальной суммы: порог задан и не
 * набран (501 — у него же лежат строки демо-корзины, поэтому в корзине видно
 * предупреждение), порог отмечен как неизвестный (502) и порог не заполнен (503).
 */
export const demoPrivateSuppliers = (): RestaurantPrivateSupplierDto[] => [
  {
    id: 501,
    legalName: 'ИП Морозов Андрей Сергеевич',
    ogrn: '316774600123454',
    inn: '',
    displayLabel: 'Прайс «Утренний рынок»',
    linkedSupplierOrganizationId: null,
    isRegisteredSupplier: false,
    minOrderAmount: 4000,
    minOrderAmountUnknown: false,
    deliverySameDay: true,
    deliveryNextDay: false,
    deliveryWeekends: true,
    managerName: 'Морозов Андрей Сергеевич',
    managerPhone: '+7 912 345-67-89',
    managerEmail: 'zakaz@utrenniy-rynok.ru',
  },
  {
    id: 502,
    legalName: 'ООО «ОвощБаза»',
    ogrn: '1127746098765',
    inn: '',
    displayLabel: null,
    linkedSupplierOrganizationId: null,
    isRegisteredSupplier: false,
    minOrderAmount: null,
    minOrderAmountUnknown: true,
    deliverySameDay: false,
    deliveryNextDay: true,
    deliveryWeekends: false,
    managerName: null,
    managerPhone: '+7 495 120-30-40',
    managerEmail: null,
  },
  {
    id: 503,
    legalName: 'ООО «Супримфуд»',
    ogrn: '1057749123453',
    inn: '',
    displayLabel: null,
    linkedSupplierOrganizationId: null,
    isRegisteredSupplier: false,
    minOrderAmount: null,
    minOrderAmountUnknown: false,
    deliverySameDay: false,
    deliveryNextDay: false,
    deliveryWeekends: false,
    managerName: null,
    managerPhone: null,
    managerEmail: null,
  },
]

export const demoBuyerMe = (): MeResponse => ({
  id: 1001,
  email: 'demo.buyer@provisium.local',
  displayName: 'Демо Покупатель',
  first_name: 'Демо',
  last_name: 'Покупатель',
  phone: '+7 900 000-00-00',
  roles: ['BUYER'],
  organizations: [
    {
      id: 10,
      org_type: 'buyer',
      legal_name: 'ООО Демо Ресторан',
      trade_name: 'Демо Ресторан',
      ogrn: '1027700132195',
      city: 'moscow',
      horeca_type: 'restaurant',
      can_edit: true,
      member_role: 'owner',
      // Контактное лицо заполнено, чтобы блок в настройках можно было посмотреть локально.
      contact_person_enabled: true,
      contact_name: 'Иван Петров',
      contact_phone: '+7 900 000-00-00',
      plan: 'year',
      plan_display_name: 'Год',
      market_catalog_enabled: true,
      platform_rfq_enabled: true,
      platform_chat_enabled: true,
      buyer_suppliers_enabled: true,
      market_price_intelligence_enabled: true,
      venues_limit: 2,
      venues_used: 1,
      paid_venue_slots: 0,
      venues: [
        {
          id: 1,
          name: 'Основная точка',
          sort_order: 0,
          active: true,
        },
      ],
    },
  ],
})

export const demoBuyerSession = (): AuthSession => ({
  accessToken: 'demo-access-token',
  refreshToken: 'demo-refresh-token',
  organizationId: 10,
  email: 'demo.buyer@provisium.local',
  displayName: 'Демо Покупатель',
})

export const demoPriceMoves = (): PriceMoveDto[] => [
  {
    catalog_item_id: 101,
    organization_id: 20,
    product_type_id: 17,
    unit: 'кг',
    previous_price: 312,
    current_price: 289,
    delta_abs: -23,
    delta_pct: -7.4,
    observed_at: iso(0),
    is_private: true,
    private_supplier_id: 1,
    supplier_name: 'АгроСнаб',
    item_name: 'Филе куриное охл.',
  },
  {
    catalog_item_id: 204,
    organization_id: 21,
    product_type_id: 34,
    unit: 'кг',
    previous_price: 42,
    current_price: 48.5,
    delta_abs: 6.5,
    delta_pct: 15.5,
    observed_at: iso(1),
    is_private: true,
    private_supplier_id: 2,
    supplier_name: 'ОвощТорг',
    item_name: 'Картофель мытый',
  },
  {
    catalog_item_id: 310,
    organization_id: 22,
    product_type_id: 2,
    unit: 'кг',
    previous_price: 890,
    current_price: 845,
    delta_abs: -45,
    delta_pct: -5.1,
    observed_at: iso(1),
    is_private: true,
    private_supplier_id: 1,
    supplier_name: 'АгроСнаб',
    item_name: 'Лосось с/м',
  },
  {
    catalog_item_id: 118,
    organization_id: 20,
    product_type_id: 17,
    unit: 'кг',
    previous_price: 265,
    current_price: 278,
    delta_abs: 13,
    delta_pct: 4.9,
    observed_at: iso(2),
    is_private: true,
    private_supplier_id: 2,
    supplier_name: 'ОвощТорг',
    item_name: 'Филе лосося с/м',
  },
  {
    catalog_item_id: 401,
    organization_id: 21,
    product_type_id: 40,
    unit: 'пучок',
    previous_price: 95,
    current_price: 79,
    delta_abs: -16,
    delta_pct: -16.8,
    observed_at: iso(3),
    is_private: true,
    private_supplier_id: 1,
    supplier_name: 'АгроСнаб',
    item_name: 'Укроп пучок',
  },
]

export const demoCategoryBands = (productTypeId: number): CategoryBandDto[] => {
  const base =
    productTypeId === 34 ? 44 : productTypeId === 2 ? 860 : productTypeId === 40 ? 88 : 290
  return Array.from({ length: 28 }, (_, index) => {
    const daysAgo = 27 - index
    const wobble = Math.sin(index / 3.5) * (base * 0.05)
    const avg = base + wobble
    const date = new Date()
    date.setUTCDate(date.getUTCDate() - daysAgo)
    return {
      band_date: date.toISOString().slice(0, 10),
      product_type_id: productTypeId,
      source_kind: 'list',
      unit: 'кг',
      min_price: Number((avg * 0.88).toFixed(2)),
      max_price: Number((avg * 1.14).toFixed(2)),
      avg_price: Number(avg.toFixed(2)),
      median_price: Number((avg * 0.98).toFixed(2)),
      sample_count: 8 + (index % 5),
    }
  })
}
